// VariationsCard — commercial change: variations with their value impacts,
// and the statements of work that make up part of the contract value.
//
// The section's `totalCount` and `truncated` describe the VARIATIONS array
// only. The truncation note therefore sits under the variations table and is
// worded to scope itself there — a note under the SOW table, or one that read
// "showing 50 of 61 records", would imply the SOW list had been cut when it
// never is.
//
// Value impacts render through the shared signed-delta cell: explicit sign,
// Unicode minus, GST basis, and a non-financial null kept visibly distinct
// from an explicit zero.

import MetricCard from "../tracking/deliverable/MetricCard";
import ValueImpactCell from "../tracking/variations-sows/ValueImpactCell";
import ReportSection, {
  NothingToReport,
  UnavailableNotice,
} from "./ReportSection";
import { SowStatusBadge, VariationStatusBadge } from "./reportStatusBadges";
import { formatAud2dp } from "../../../utils/formatCurrency";
import { formatDayMonthYear } from "../../../utils/formatShortDate";
import type {
  SectionUnavailable,
  SummaryVariationsSection,
} from "../../../types/report";

const SECTION_LABEL = "Variations and SOWs";

// The report model states its own basis (`gstBasis: 'ex-GST'`), so the shared
// cell is told GST applies in order to render the "ex-GST" suffix. It has no
// contract record to read a per-contract flag from, and must not invent one.
const REPORT_VALUES_ARE_EX_GST = true;

export interface VariationsCardProps {
  section: SummaryVariationsSection | SectionUnavailable;
}

/** A statement of work can carry one end of its period without the other.
 *  Joining unconditionally left a dangling "–" with nothing on one side,
 *  which reads as a missing value rather than an open-ended period. */
function sowPeriod(startDate: string | null, endDate: string | null): string {
  const parts = [startDate, endDate]
    .filter((d): d is string => d !== null)
    .map(formatDayMonthYear);
  if (parts.length === 0) return "—";
  return parts.join(" – ");
}

export default function VariationsCard({ section }: VariationsCardProps) {
  if (section.status === "unavailable") {
    return (
      <ReportSection id="variations" title={SECTION_LABEL}>
        <UnavailableNotice label={SECTION_LABEL} />
      </ReportSection>
    );
  }

  if (section.variations.length === 0 && section.sows.length === 0) {
    return (
      <ReportSection id="variations" title={SECTION_LABEL}>
        <NothingToReport />
      </ReportSection>
    );
  }

  return (
    <ReportSection id="variations" title={SECTION_LABEL}>
      <div className="sm:max-w-xs">
        <MetricCard
          testId="variations-approved-impact"
          label="Approved value impact"
          value={formatAud2dp(section.approvedValueImpact)}
          subtitle={section.gstBasis}
        />
      </div>

      <div className="mt-4">
        <p className="text-caption font-medium text-slate-500 dark:text-slate-400">
          Variations
        </p>
        {section.variations.length === 0 ? (
          <p className="mt-2 text-body-sm text-slate-500 dark:text-slate-400">
            No variations recorded
          </p>
        ) : (
          <>
            <div
              className="mt-2 overflow-x-auto"
              tabIndex={0}
              role="group"
              aria-label="Variations"
            >
              <table className="w-full">
                <caption className="sr-only">Variations</caption>
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
                    <th className="px-3 py-2 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                      Variation
                    </th>
                    <th className="px-3 py-2 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                      Status
                    </th>
                    <th className="px-3 py-2 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                      Effective
                    </th>
                    <th className="px-3 py-2 text-right text-overline uppercase text-slate-600 dark:text-slate-400">
                      Value impact
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {section.variations.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b border-slate-100 dark:border-slate-700"
                    >
                      <td className="px-3 py-2 text-body-sm text-slate-900 dark:text-slate-100">
                        {row.name}
                      </td>
                      <td className="px-3 py-2">
                        <VariationStatusBadge status={row.variationStatus} />
                      </td>
                      <td
                        data-testid={`variation-effective-${row.id}`}
                        className="px-3 py-2 text-body-sm tabular-nums text-slate-900 dark:text-slate-100"
                      >
                        {row.effectiveDate === null
                          ? "—"
                          : formatDayMonthYear(row.effectiveDate)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <ValueImpactCell
                          testId={`variation-impact-${row.id}`}
                          value={row.valueImpact}
                          gstApplies={REPORT_VALUES_ARE_EX_GST}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {section.truncated && (
              <p
                data-testid="variations-truncation-note"
                className="mt-2 text-caption text-slate-500 dark:text-slate-400"
              >
                Showing {section.variations.length} of {section.totalCount}{" "}
                variations
              </p>
            )}
          </>
        )}
      </div>

      <div className="mt-5">
        <p className="text-caption font-medium text-slate-500 dark:text-slate-400">
          Statements of work
        </p>
        {section.sows.length === 0 ? (
          <p className="mt-2 text-body-sm text-slate-500 dark:text-slate-400">
            No statements of work recorded
          </p>
        ) : (
          <div
            className="mt-2 overflow-x-auto"
            tabIndex={0}
            role="group"
            aria-label="Statements of work"
          >
            <table className="w-full">
              <caption className="sr-only">Statements of work</caption>
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
                  <th className="px-3 py-2 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                    No.
                  </th>
                  <th className="px-3 py-2 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                    Statement of work
                  </th>
                  <th className="px-3 py-2 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                    Status
                  </th>
                  <th className="px-3 py-2 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                    Period
                  </th>
                  <th className="px-3 py-2 text-right text-overline uppercase text-slate-600 dark:text-slate-400">
                    Original value
                  </th>
                </tr>
              </thead>
              <tbody>
                {section.sows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-slate-100 dark:border-slate-700"
                  >
                    <td
                      data-testid={`sow-number-${row.id}`}
                      className="px-3 py-2 font-mono text-body-sm text-slate-500 dark:text-slate-400"
                    >
                      {row.number === null ? "—" : row.number}
                    </td>
                    <td className="px-3 py-2 text-body-sm text-slate-900 dark:text-slate-100">
                      {row.name}
                    </td>
                    <td className="px-3 py-2">
                      <SowStatusBadge status={row.sowStatus} />
                    </td>
                    <td
                      data-testid={`sow-period-${row.id}`}
                      className="px-3 py-2 text-body-sm tabular-nums text-slate-900 dark:text-slate-100"
                    >
                      {sowPeriod(row.startDate, row.endDate)}
                    </td>
                    <td className="px-3 py-2 text-right text-body-sm tabular-nums text-slate-900 dark:text-slate-100">
                      {formatAud2dp(row.originalValue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ReportSection>
  );
}
